import React, { useState, useEffect, ChangeEvent } from 'react';
import { useThemeContext } from 'context/ThemeContext/ThemeContext';
import { CornerStyle } from 'context/ThemeContext/ThemeContext';
import { useMask } from '@react-input/mask';

type TelInputProps = {
  name?: string;
  className?: string;
  value: string | undefined;
  mask?: string;
  styles?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
};

const TelInput: React.FC<TelInputProps> = ({
  name,
  value,
  styles,
  onChange,
  required,
  mask = '',
  className,
  placeholder = '',
  disabled = false,
  autoFocus = false,
}) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const { inputStyle } = useThemeContext();

  const defaultMask = '(+1) ___ ___ ____';
  const maskToUse = mask || defaultMask;
  const transformedMask = maskToUse.replace(/(?<!\\)9/g, '_');

  const inputRef = useMask({
    mask: transformedMask,
    replacement: { _: /\d/ },
    showMask: true,
    separate: true,
  });

  useEffect(() => {
    if (
      !value ||
      value.trim() === '' ||
      (!value.includes('_') && value.length <= 5)
    ) {
      const fullMask = transformedMask;
      if (onChange) {
        const syntheticEvent = {
          target: { value: fullMask },
        } as ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    }
  }, []);

  useEffect(() => {
    if (value) setIsFocused(true);
    else setIsFocused(false);
  }, [value]);

  const getFirstUnfilledPosition = (val: string): number => {
    const idx = val.indexOf('_');
    return idx !== -1 ? idx : val.length;
  };

  useEffect(() => {
    if (autoFocus && inputRef.current && !disabled) {
      setTimeout(() => {
        inputRef.current?.focus();
        const pos = getFirstUnfilledPosition(
          inputRef.current?.value || transformedMask
        );
        inputRef.current?.setSelectionRange(pos, pos);
      }, 0);
    }
  }, [autoFocus, disabled]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value;
    if (!newValue.startsWith('(+1)')) {
      if (newValue.length < 5) {
        newValue = transformedMask;
      } else {
        newValue = '(+1) ' + newValue.replace(/^\(\+1\)\s*/, '');
      }
    }

    if (onChange) {
      const syntheticEvent = {
        ...event,
        target: {
          ...event.target,
          value: newValue,
        },
      } as ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  const getCornerStyle = () => {
    switch (inputStyle) {
      case CornerStyle.FULL_ROUND:
        return 'rounded-full';
      case CornerStyle.ROUNDED_CORNERS:
        return 'rounded-[10px]';
      case CornerStyle.SQUARE_CORNERS:
        return 'rounded-none';
      default:
        return 'rounded-full';
    }
  };

  return (
    <div
      className={`
        relative w-full h-input flex items-center px-6 border-2 border-solid border-input transition-all ease-linear duration-300
        [&_input]:z-20 [&_input]:w-full [&_input]:border-0 [&_input]:bg-transparent
        [&_input]:active:bg-transparent [&_input]:active:outline-none
        [&_input]:hover:bg-transparent [&_input]:hover:outline-none
        ${isFocused ? 'bg-white' : 'bg-lightgray'}
        ${disabled && 'opacity-70'}
        ${getCornerStyle()}
        ${styles}
      `}
    >
      <span
        className={`z-40 absolute text-muted transition-all duration-300 pointer-events-none ${
          isFocused
            ? 'text-xxxs px-1 bg-white -translate-x-0.5 -translate-y-6 opacity-100 rounded'
            : 'text-[0.9rem] bg-transparent translate-x-0 translate-y-0 opacity-80'
        }`}
      >
        {placeholder}
      </span>
      <input
        ref={inputRef}
        type='tel'
        name={name}
        value={value || transformedMask}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        autoComplete='tel'
        aria-label={placeholder}
        onChange={handleChange}
        onFocus={(event) => {
          setIsFocused(true);
          const target = event.target;
          setTimeout(() => {
            const pos = getFirstUnfilledPosition(target.value);
            target.setSelectionRange(pos, pos);
          }, 0);
        }}
        onClick={(event) => {
          const pos = getFirstUnfilledPosition(event.currentTarget.value);
          event.currentTarget.setSelectionRange(pos, pos);
        }}
        onBlur={() => !value && setIsFocused(false)}
        className={`disabled:pointer-events-none disabled:text-disbaledInput ${
          className || ''
        }`}
      />
    </div>
  );
};

export default TelInput;
